import { useState, useEffect } from 'react';
import api from '../api/axios';
import Layout from '../components/Layout';
import { usePermissions } from '../hooks/usePermissions';
import PaymentLinkModal from '../components/PaymentLinkModal';
import Toast from '../components/Toast';
import { SkeletonTable } from '../components/Skeleton';
import { formatCurrency as fmt } from '../utils/currency';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

const C = {
  ink: '#0B0E17', surface: '#1E2235', border: '#2A2E45',
  muted: '#8892A4', gold: '#F0B429', teal: '#00C9B1',
  coral: '#FF6B6B', sky: '#38BDF8', text: '#E8EDF5', sub: '#C5CDD8',
};

const STATUS_CONFIG = {
  completed: { label: 'Paid',      bg: 'rgba(0,201,177,0.12)',   color: '#00C9B1', border: 'rgba(0,201,177,0.3)'   },
  unpaid:    { label: 'Unpaid',    bg: 'rgba(255,107,107,0.12)', color: '#FF6B6B', border: 'rgba(255,107,107,0.3)' },
  partial:   { label: 'Partial',   bg: 'rgba(240,180,41,0.12)',  color: '#F0B429', border: 'rgba(240,180,41,0.3)'  },
  pending:   { label: 'Pending',   bg: 'rgba(240,180,41,0.1)',   color: '#F0B429', border: 'rgba(240,180,41,0.25)' },
  cancelled: { label: 'Cancelled', bg: 'rgba(136,146,164,0.1)',  color: '#8892A4', border: 'rgba(136,146,164,0.2)' },
  refunded:  { label: 'Refunded',  bg: 'rgba(56,189,248,0.1)',   color: '#38BDF8', border: 'rgba(56,189,248,0.25)' },
};

const RETURN_STATUS_CONFIG = {
  partial:  { label: 'Returned',       bg: 'rgba(240,180,41,0.1)',   color: '#F0B429', border: 'rgba(240,180,41,0.25)'  },
  refunded: { label: 'Fully Returned', bg: 'rgba(56,189,248,0.1)',   color: '#38BDF8', border: 'rgba(56,189,248,0.25)'  },
};

const RETURN_REASONS = [
  { id: 'customer_return',   label: 'Customer return'   },
  { id: 'damaged_on_return', label: 'Damaged on return' },
  { id: 'wrong_item',        label: 'Wrong item sold'   },
  { id: 'pricing_issue',     label: 'Pricing issue'     },
];

const STYLES = `
  @keyframes sl-modal-enter { from { opacity: 0; transform: scale(0.96) translateY(8px); } to { opacity: 1; transform: scale(1) translateY(0); } }
  .sl-page { font-family: 'Cabinet Grotesk', sans-serif; }
  .sl-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; padding: 28px 28px 0; flex-wrap: wrap; }
  .sl-title { font-size: 1.55rem; font-weight: 800; color: ${C.text}; margin: 0; letter-spacing: -0.3px; }
  .sl-count { font-size: 0.72rem; color: ${C.muted}; margin-top: 3px; }
  .sl-btn-primary { background: ${C.gold}; color: ${C.ink}; border: none; border-radius: 10px; padding: 10px 18px; font-size: 0.84rem; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 6px; font-family: inherit; transition: opacity 150ms; white-space: nowrap; }
  .sl-btn-primary:hover { opacity: 0.88; }
  .sl-body { padding: 24px 28px; }
  .sl-toolbar { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; flex-wrap: wrap; }
  .sl-select { padding: 8px 13px; background: rgba(255,255,255,0.05); border: 1px solid ${C.border}; border-radius: 9px; font-size: 0.84rem; color: ${C.sub}; font-family: inherit; outline: none; cursor: pointer; }
  .sl-select:focus { border-color: ${C.teal}; color: ${C.text}; }
  .sl-toolbar-count { font-size: 0.82rem; color: ${C.muted}; }
  .sl-wrap { background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 16px; overflow: hidden; }
  .sl-table { width: 100%; border-collapse: collapse; }
  .sl-table thead th { padding: 12px 16px; font-size: 0.7rem; font-weight: 700; color: ${C.muted}; letter-spacing: 0.06em; text-transform: uppercase; text-align: left; border-bottom: 1px solid ${C.border}; background: rgba(255,255,255,0.02); white-space: nowrap; }
  .sl-table tbody tr { border-bottom: 1px solid ${C.border}; transition: background 150ms; }
  .sl-table tbody tr:last-child { border-bottom: none; }
  .sl-table tbody tr:hover { background: rgba(255,255,255,0.025); }
  .sl-td { padding: 13px 16px; vertical-align: middle; font-size: 0.875rem; color: ${C.text}; }
  .sl-empty { padding: 60px 20px; text-align: center; }
  .sl-btn-sm { padding: 5px 11px; border-radius: 7px; font-size: 0.72rem; font-weight: 700; cursor: pointer; border: 1px solid ${C.border}; background: rgba(255,255,255,0.05); color: ${C.sub}; font-family: inherit; transition: all 140ms; white-space: nowrap; }
  .sl-btn-sm:hover { background: rgba(255,255,255,0.09); color: ${C.text}; }
  .sl-btn-sm:disabled { opacity: 0.5; cursor: not-allowed; }
  .sl-btn-sm-pdf { border-color: rgba(56,189,248,0.3); color: ${C.sky}; background: rgba(56,189,248,0.07); }
  .sl-btn-sm-pdf:hover { background: rgba(56,189,248,0.13); }
  .sl-btn-sm-email { border-color: rgba(0,201,177,0.3); color: ${C.teal}; background: rgba(0,201,177,0.07); }
  .sl-btn-sm-email:hover { background: rgba(0,201,177,0.13); }
  .sl-btn-sm-email-off { border-color: ${C.border}; color: ${C.muted}; background: rgba(255,255,255,0.03); cursor: not-allowed; opacity: 0.6; }
  .sl-btn-sm-paylink { border-color: rgba(240,180,41,0.3); color: ${C.gold}; background: rgba(240,180,41,0.07); }
  .sl-btn-sm-paylink:hover { background: rgba(240,180,41,0.13); }
  .sl-btn-sm-danger { border-color: rgba(255,107,107,0.3); color: ${C.coral}; background: rgba(255,107,107,0.07); }
  .sl-btn-sm-danger:hover { background: rgba(255,107,107,0.13); }
  .sl-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.65); backdrop-filter: blur(4px); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 20px; }
  .sl-modal { background: ${C.surface}; border: 1px solid ${C.border}; border-radius: 18px; width: 100%; padding: 0; box-shadow: 0 24px 60px rgba(0,0,0,0.55); max-height: 90vh; overflow-y: auto; animation: sl-modal-enter 280ms cubic-bezier(0.34,1.56,0.64,1) forwards; }
  .sl-modal-header { display: flex; justify-content: space-between; align-items: center; padding: 22px 24px 0; margin-bottom: 4px; }
  .sl-modal-title { font-size: 1.1rem; font-weight: 800; color: ${C.text}; margin: 0; }
  .sl-modal-close { background: rgba(255,255,255,0.05); border: 1px solid ${C.border}; color: ${C.muted}; font-size: 1rem; cursor: pointer; padding: 6px 10px; border-radius: 8px; line-height: 1; font-family: inherit; transition: all 150ms; }
  .sl-modal-close:hover { background: rgba(255,255,255,0.09); color: ${C.text}; }
  .sl-modal-body { padding: 16px 24px; }
  .sl-alert-err { background: rgba(255,107,107,0.1); border: 1px solid rgba(255,107,107,0.3); border-radius: 10px; padding: 10px 14px; font-size: 0.82rem; color: ${C.coral}; margin: 0 24px 12px; }
  .sl-form-group { margin-bottom: 16px; }
  .sl-form-label { display: block; font-size: 0.72rem; font-weight: 700; color: ${C.muted}; margin-bottom: 6px; letter-spacing: 0.04em; text-transform: uppercase; }
  .sl-input { width: 100%; padding: 10px 13px; background: rgba(255,255,255,0.04); border: 1.5px solid ${C.border}; border-radius: 10px; font-size: 0.875rem; color: ${C.text}; font-family: inherit; outline: none; box-sizing: border-box; transition: border-color 150ms; }
  .sl-input:focus { border-color: ${C.teal}; }
  .sl-input::placeholder { color: ${C.muted}; }
  .sl-select-full { width: 100%; padding: 10px 13px; background: rgba(255,255,255,0.04); border: 1.5px solid ${C.border}; border-radius: 10px; font-size: 0.875rem; color: ${C.text}; font-family: inherit; outline: none; box-sizing: border-box; }
  .sl-select-full:focus { border-color: ${C.teal}; }
  .sl-textarea { width: 100%; padding: 10px 13px; background: rgba(255,255,255,0.04); border: 1.5px solid ${C.border}; border-radius: 10px; font-size: 0.875rem; color: ${C.text}; font-family: inherit; outline: none; box-sizing: border-box; resize: vertical; }
  .sl-items-box { border: 1.5px solid ${C.border}; border-radius: 10px; overflow: hidden; margin-bottom: 8px; }
  .sl-item-row { display: grid; grid-template-columns: 1fr 80px 90px auto; gap: 8px; padding: 10px 12px; align-items: center; border-bottom: 1px solid ${C.border}; }
  .sl-item-row:last-child { border-bottom: none; }
  .sl-item-row-even { background: rgba(255,255,255,0.02); }
  .sl-total-box { background: rgba(255,255,255,0.03); border-radius: 10px; padding: 14px 16px; margin-bottom: 16px; border: 1px solid ${C.border}; }
  .sl-total-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
  .sl-total-row:last-child { margin-bottom: 0; border-top: 1px solid ${C.border}; padding-top: 10px; margin-top: 8px; }
  .sl-modal-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 24px; border-top: 1px solid ${C.border}; }
  .sl-btn-cancel { padding: 10px 18px; border-radius: 10px; border: 1px solid ${C.border}; background: rgba(255,255,255,0.04); color: ${C.muted}; font-weight: 600; font-size: 0.85rem; cursor: pointer; font-family: inherit; }
  .sl-btn-cancel:hover { background: rgba(255,255,255,0.07); color: ${C.text}; }
  .sl-btn-submit { padding: 10px 22px; border-radius: 10px; border: none; background: ${C.gold}; color: ${C.ink}; font-weight: 700; font-size: 0.85rem; cursor: pointer; font-family: inherit; transition: opacity 150ms; }
  .sl-btn-submit:hover { opacity: 0.88; }
  .sl-btn-submit:disabled { opacity: 0.55; cursor: not-allowed; }
  .sl-btn-add-item { padding: 6px 12px; border-radius: 8px; border: 1px solid ${C.border}; background: rgba(255,255,255,0.04); color: ${C.muted}; font-size: 0.78rem; font-weight: 600; cursor: pointer; font-family: inherit; transition: all 140ms; }
  .sl-btn-add-item:hover { color: ${C.text}; background: rgba(255,255,255,0.08); }
  .sl-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .sl-return-info { margin-bottom: 16px; padding: 12px 14px; border-radius: 12px; background: rgba(255,255,255,0.03); border: 1px solid ${C.border}; }
  .sl-return-item-row { display: grid; grid-template-columns: 1.3fr 80px 90px 120px; gap: 8px; padding: 10px 12px; align-items: center; border-bottom: 1px solid ${C.border}; }
  .sl-return-item-row:last-child { border-bottom: none; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

export default function Sales() {
  const { can } = usePermissions();
  const canCreateSale       = can('sales:create');
  const canReturnSale       = can('sales:return');
  const canDeleteSale       = can('sales:delete');
  const canViewInventoryCosts = can('inventory:cost:view');

  const [sales, setSales]               = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal]       = useState(false);
  const [customers, setCustomers]       = useState([]);
  const [inventory, setInventory]       = useState([]);
  const [saleItems, setSaleItems]       = useState([{ itemId: '', quantity: 1 }]);
  const [customerId, setCustomerId]     = useState('');
  const [notes, setNotes]               = useState('');
  const [formError, setFormError]       = useState('');
  const [formLoading, setFormLoading]   = useState(false);
  const [btnLoading, setBtnLoading]     = useState({});
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnSale, setReturnSale]     = useState(null);
  const [returnItems, setReturnItems]   = useState([]);
  const [returnReason, setReturnReason] = useState('customer_return');
  const [returnNotes, setReturnNotes]   = useState('');
  const [refundMethod, setRefundMethod] = useState('cash');
  const [returnError, setReturnError]   = useState('');
  const [returnLoading, setReturnLoading] = useState(false);
  const [toast, setToast]               = useState(null);
  const [pageLoading, setPageLoading]   = useState(true);
  const [pageError, setPageError]       = useState('');
  const [paymentLinkSale, setPaymentLinkSale] = useState(null);

  const showToast = (message, type = 'success') => setToast({ message, type });
  const isBusy    = (id, action) => !!btnLoading[`${id}-${action}`];
  const setBusy   = (id, action, val) =>
    setBtnLoading((s) => ({ ...s, [`${id}-${action}`]: val }));

  const load = async () => {
    setPageLoading(true);
    setPageError('');
    try {
      const { data } = await api.get('/sales', { params: statusFilter ? { status: statusFilter } : {} });
      setSales(data);
    } catch {
      setPageError('Failed to load sales. Please try again.');
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const openNew = async () => {
    if (!canCreateSale) return;
    setFormError('');
    setSaleItems([{ itemId: '', quantity: 1 }]);
    setCustomerId('');
    setNotes('');
    const [cRes, iRes] = await Promise.all([api.get('/customers'), api.get('/inventory')]);
    setCustomers(cRes.data);
    setInventory(iRes.data);
    setShowModal(true);
  };

  const addLine    = () => setSaleItems((s) => [...s, { itemId: '', quantity: 1 }]);
  const removeLine = (idx) => setSaleItems((s) => s.filter((_, i) => i !== idx));
  const updateLine = (idx, field, val) =>
    setSaleItems((s) => s.map((row, i) => (i === idx ? { ...row, [field]: val } : row)));

  const calcTotal = () =>
    saleItems.reduce((sum, row) => {
      const inv = inventory.find((i) => i.id === Number(row.itemId));
      return sum + (inv ? Number(inv.price) * Number(row.quantity || 0) : 0);
    }, 0);

  const calcCOGS = () =>
    saleItems.reduce((sum, row) => {
      const inv = inventory.find((i) => i.id === Number(row.itemId));
      return sum + (inv ? Number(inv.cost) * Number(row.quantity || 0) : 0);
    }, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    const valid = saleItems.filter((r) => r.itemId && r.quantity > 0);
    if (!valid.length) { setFormError('Add at least one item'); return; }
    setFormLoading(true);
    try {
      await api.post('/sales', {
        customerId: parseInt(customerId) || null,
        items: valid.map((r) => ({ itemId: parseInt(r.itemId) || 0, quantity: parseInt(r.quantity) || 0 })),
        notes,
      });
      setShowModal(false);
      load();
      showToast('Sale recorded successfully');
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to create sale');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDownload = async (sale) => {
    setBusy(sale.id, 'pdf', true);
    try {
      const tok = localStorage.getItem('token');
      const res  = await fetch(`${API_BASE}/invoices/${sale.id}`, { headers: { Authorization: `Bearer ${tok}` } });
      if (!res.ok) throw new Error('Failed to generate PDF');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `INV-${String(sale.id).padStart(4, '0')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[Sales] PDF generation error:', err);
      showToast('Failed to generate PDF. Please try again.', 'error');
    } finally {
      setBusy(sale.id, 'pdf', false);
    }
  };

  const handlePreview = (sale) => {
    const tok = localStorage.getItem('token');
    window.open(`${API_BASE}/invoices/${sale.id}/preview?token=${tok}`, '_blank');
  };

  const handleEmail = async (sale) => {
    if (!sale.customer?.email) { showToast('No customer email on file', 'error'); return; }
    setBusy(sale.id, 'email', true);
    try {
      await api.post(`/invoices/${sale.id}/email`);
      showToast(`Invoice emailed to ${sale.customer.email}`);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to send email', 'error');
    } finally {
      setBusy(sale.id, 'email', false);
    }
  };

  const handleDelete = async (id) => {
    if (!canDeleteSale) return;
    if (!confirm('Delete this sale?')) return;
    await api.delete(`/sales/${id}`);
    load();
  };

  const getAlreadyReturnedQty = (saleItem) => (
    Array.isArray(saleItem.returnItems)
      ? saleItem.returnItems.reduce((sum, entry) => sum + Number(entry.quantity || 0), 0)
      : 0
  );

  const openReturnModal = async (saleId) => {
    if (!canReturnSale) return;
    setReturnLoading(true);
    setReturnError('');
    try {
      const { data } = await api.get(`/sales/${saleId}`);
      setReturnSale(data);
      setReturnItems((data.items || []).map((item) => ({
        saleItemId: item.id,
        name: item.item?.name || `Item #${item.itemId}`,
        unitName: item.unitName || 'Unit',
        soldQty: Number(item.quantity || 0),
        returnedQty: getAlreadyReturnedQty(item),
        unitPrice: Number(item.price || 0),
        quantity: 0,
      })));
      setReturnReason('customer_return');
      setReturnNotes('');
      setRefundMethod('cash');
      setShowReturnModal(true);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to load sale for return', 'error');
    } finally {
      setReturnLoading(false);
    }
  };

  const closeReturnModal = (force = false) => {
    if (returnLoading && !force) return;
    setShowReturnModal(false);
    setReturnSale(null);
    setReturnItems([]);
    setReturnReason('customer_return');
    setReturnNotes('');
    setRefundMethod('cash');
    setReturnError('');
  };

  const updateReturnItemQty = (saleItemId, quantity) => {
    setReturnItems((cur) => cur.map((item) => item.saleItemId === saleItemId ? { ...item, quantity } : item));
  };

  const handleReturnSubmit = async (e) => {
    e.preventDefault();
    if (!returnSale) return;
    const selectedItems = returnItems
      .map((item) => ({ saleItemId: item.saleItemId, quantity: Number(item.quantity || 0) }))
      .filter((item) => item.quantity > 0);
    if (!selectedItems.length) { setReturnError('Select at least one item quantity to return.'); return; }
    setReturnLoading(true);
    setReturnError('');
    try {
      const { data } = await api.post(`/sales/${returnSale.id}/returns`, {
        items: selectedItems, reason: returnReason, notes: returnNotes, refundMethod,
      });
      closeReturnModal(true);
      await load();
      showToast(data.refundAmount > 0 ? `Return recorded. ${fmt(data.refundAmount)} refunded.` : 'Return recorded. Outstanding balance updated.');
    } catch (err) {
      setReturnError(err.response?.data?.error || 'Failed to process return');
    } finally {
      setReturnLoading(false);
    }
  };

  const total  = calcTotal();
  const cogs   = canViewInventoryCosts ? calcCOGS() : 0;
  const margin = canViewInventoryCosts && total > 0 ? (((total - cogs) / total) * 100).toFixed(0) : 0;

  return (
    <Layout>
      <style>{STYLES}</style>
      <div className="sl-page">
        <div className="sl-header">
          <div>
            <h2 className="sl-title">Sales</h2>
            {sales.length > 0 && <div className="sl-count">{sales.length} transaction{sales.length !== 1 ? 's' : ''}</div>}
          </div>
          {canCreateSale && (
            <button className="sl-btn-primary" onClick={openNew}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              New Sale
            </button>
          )}
        </div>

        <div className="sl-body">
          <div className="sl-toolbar">
            <select className="sl-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 180 }}>
              <option value="">All Statuses</option>
              <option value="completed">Paid</option>
              <option value="unpaid">Unpaid</option>
              <option value="partial">Partial</option>
              <option value="pending">Pending</option>
              <option value="cancelled">Cancelled</option>
              <option value="refunded">Refunded</option>
            </select>
            <span className="sl-toolbar-count">{sales.length} sale{sales.length !== 1 ? 's' : ''}</span>
          </div>

          <div className="sl-wrap">
            {pageLoading ? (
              <SkeletonTable rows={6} cols={7} />
            ) : pageError ? (
              <div className="sl-empty">
                <p style={{ color: C.coral, marginBottom: 12 }}>{pageError}</p>
                <button className="sl-btn-cancel" onClick={load}>Retry</button>
              </div>
            ) : sales.length === 0 ? (
              <div className="sl-empty">
                <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🧾</div>
                <p style={{ fontWeight: 600, color: C.sub, marginBottom: 6 }}>No sales yet</p>
                <p style={{ fontSize: '0.875rem', color: C.muted, marginBottom: 16 }}>Record your first sale to get started</p>
                {canCreateSale
                  ? <button className="sl-btn-primary" onClick={openNew} style={{ margin: '0 auto' }}>Create your first sale</button>
                  : <div style={{ fontSize: '0.84rem', color: C.muted }}>You can view sales here, but manual sale entry is disabled for your role.</div>}
              </div>
            ) : (
              <table className="sl-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Customer</th>
                    <th>Items</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((sale) => {
                    const sc = STATUS_CONFIG[sale.status] || { label: sale.status, bg: 'rgba(136,146,164,0.1)', color: C.muted, border: C.border };
                    const rc = sale.returnStatus && sale.returnStatus !== 'none' ? RETURN_STATUS_CONFIG[sale.returnStatus] : null;
                    return (
                      <tr key={sale.id}>
                        <td className="sl-td" style={{ color: C.muted, fontFamily: "'JetBrains Mono', monospace", fontSize: '0.82rem' }}>
                          #{String(sale.id).padStart(4, '0')}
                        </td>
                        <td className="sl-td" style={{ fontWeight: 500 }}>
                          {sale.customer?.name || <span style={{ color: C.muted }}>Walk-in</span>}
                        </td>
                        <td className="sl-td" style={{ color: C.muted }}>
                          {sale.items.length} item{sale.items.length !== 1 ? 's' : ''}
                        </td>
                        <td className="sl-td" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                          <span style={{ fontWeight: 700, color: C.teal }}>{fmt(sale.total)}</span>
                          {Number(sale.totalDiscount || 0) > 0 && (
                            <div style={{ fontSize: '0.68rem', color: C.gold, marginTop: 3 }}>Discount {fmt(sale.totalDiscount)}</div>
                          )}
                          {Number(sale.returnedAmount || 0) > 0 && (
                            <div style={{ fontSize: '0.68rem', color: C.coral, marginTop: 3 }}>Returned {fmt(sale.returnedAmount)}</div>
                          )}
                        </td>
                        <td className="sl-td">
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'flex-start' }}>
                            <span style={{ display: 'inline-block', fontSize: '0.68rem', fontWeight: 700, padding: '3px 9px', borderRadius: 20, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>
                              {sc.label}
                            </span>
                            {rc && (
                              <span style={{ display: 'inline-block', fontSize: '0.64rem', fontWeight: 800, padding: '3px 9px', borderRadius: 20, background: rc.bg, color: rc.color, border: `1px solid ${rc.border}` }}>
                                {rc.label}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="sl-td" style={{ color: C.muted, fontSize: '0.82rem' }}>
                          {new Date(sale.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </td>
                        <td className="sl-td">
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <button className="sl-btn-sm sl-btn-sm-pdf" onClick={() => handleDownload(sale)} disabled={isBusy(sale.id, 'pdf')} title="Download PDF invoice">
                              {isBusy(sale.id, 'pdf') ? '…' : '⬇ PDF'}
                            </button>
                            <button className="sl-btn-sm" onClick={() => handlePreview(sale)} title="Preview invoice">
                              👁 Preview
                            </button>
                            <button
                              className={`sl-btn-sm ${sale.customer?.email ? 'sl-btn-sm-email' : 'sl-btn-sm-email-off'}`}
                              onClick={() => handleEmail(sale)}
                              disabled={isBusy(sale.id, 'email') || !sale.customer?.email}
                              title={sale.customer?.email ? 'Email invoice' : 'No email on file'}
                            >
                              {isBusy(sale.id, 'email') ? '…' : '✉ Email'}
                            </button>
                            {canReturnSale && (
                              <button
                                className="sl-btn-sm"
                                onClick={() => openReturnModal(sale.id)}
                                disabled={sale.status === 'cancelled' || sale.status === 'refunded' || sale.returnStatus === 'refunded'}
                                title="Return items from this sale"
                              >
                                ↺ Return
                              </button>
                            )}
                            {Number(sale.balanceDue || 0) > 0 && (
                              <button className="sl-btn-sm sl-btn-sm-paylink" onClick={() => setPaymentLinkSale(sale)} title="Send customer a payment link">
                                💳 Pay link
                              </button>
                            )}
                            {canDeleteSale && (
                              <button className="sl-btn-sm sl-btn-sm-danger" onClick={() => handleDelete(sale.id)}>🗑</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* New Sale Modal */}
        {showModal && (
          <div className="sl-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
            <div className="sl-modal" style={{ maxWidth: 640 }}>
              <div className="sl-modal-header">
                <h3 className="sl-modal-title">Record Sale</h3>
                <button className="sl-modal-close" onClick={() => setShowModal(false)}>✕</button>
              </div>
              {formError && <div className="sl-alert-err">{formError}</div>}
              <form onSubmit={handleSubmit}>
                <div className="sl-modal-body">
                  <div className="sl-form-group">
                    <label className="sl-form-label">Customer (optional)</label>
                    <select className="sl-select-full" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
                      <option value="">Walk-in / No customer</option>
                      {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>

                  <div className="sl-form-group">
                    <label className="sl-form-label">Items</label>
                    <div className="sl-items-box">
                      {saleItems.map((row, idx) => {
                        const inv = inventory.find((i) => i.id === Number(row.itemId));
                        return (
                          <div key={idx} className={`sl-item-row${idx % 2 === 0 ? ' sl-item-row-even' : ''}`}>
                            <select className="sl-select-full" value={row.itemId} onChange={(e) => updateLine(idx, 'itemId', e.target.value)}>
                              <option value="">Select item...</option>
                              {inventory.map((i) => (
                                <option key={i.id} value={i.id} disabled={i.quantity === 0}>
                                  {i.name} ({i.quantity} in stock) — {fmt(i.price)}
                                </option>
                              ))}
                            </select>
                            <input className="sl-input" type="number" min="1" max={inv?.quantity || 999} value={row.quantity} onChange={(e) => updateLine(idx, 'quantity', e.target.value)} style={{ textAlign: 'center' }} />
                            <div style={{ fontSize: '0.875rem', fontWeight: 700, textAlign: 'right', color: C.teal, fontFamily: "'JetBrains Mono', monospace" }}>
                              {inv ? fmt(Number(inv.price) * Number(row.quantity || 0)) : '—'}
                            </div>
                            {saleItems.length > 1 && (
                              <button type="button" onClick={() => removeLine(idx)} style={{ background: 'none', border: 'none', color: C.coral, cursor: 'pointer', fontSize: '1rem', padding: '0 4px' }}>✕</button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <button type="button" className="sl-btn-add-item" onClick={addLine}>+ Add Item</button>
                  </div>

                  <div className="sl-total-box">
                    {canViewInventoryCosts && (
                      <>
                        <div className="sl-total-row">
                          <span style={{ fontSize: '0.82rem', color: C.muted }}>COGS</span>
                          <span style={{ fontSize: '0.875rem', color: C.muted, fontFamily: "'JetBrains Mono', monospace" }}>{fmt(cogs)}</span>
                        </div>
                        <div className="sl-total-row">
                          <span style={{ fontSize: '0.82rem', color: C.muted }}>Gross Margin</span>
                          <span style={{ fontSize: '0.875rem', color: C.teal, fontWeight: 600 }}>{margin}%</span>
                        </div>
                      </>
                    )}
                    <div className="sl-total-row">
                      <span style={{ fontWeight: 600, color: C.sub }}>Total</span>
                      <span style={{ fontSize: '1.3rem', fontWeight: 800, color: C.teal, fontFamily: "'JetBrains Mono', monospace" }}>{fmt(total)}</span>
                    </div>
                  </div>

                  <div className="sl-form-group">
                    <label className="sl-form-label">Notes</label>
                    <textarea className="sl-textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
                  </div>
                </div>

                <div className="sl-modal-footer">
                  <button type="button" className="sl-btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="sl-btn-submit" disabled={formLoading}>
                    {formLoading
                      ? <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ width: 14, height: 14, border: '2px solid rgba(11,14,23,0.25)', borderTopColor: C.ink, borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
                          Processing...
                        </span>
                      : `Record Sale · ${fmt(total)}`}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Return Items Modal */}
        {showReturnModal && returnSale && (
          <div className="sl-overlay" onClick={(e) => e.target === e.currentTarget && closeReturnModal()}>
            <div className="sl-modal" style={{ maxWidth: 720 }}>
              <div className="sl-modal-header">
                <h3 className="sl-modal-title">Return Items</h3>
                <button className="sl-modal-close" onClick={closeReturnModal}>✕</button>
              </div>
              {returnError && <div className="sl-alert-err">{returnError}</div>}
              <form onSubmit={handleReturnSubmit}>
                <div className="sl-modal-body">
                  <div className="sl-return-info">
                    <div style={{ fontWeight: 700, color: C.text }}>
                      Sale #{String(returnSale.id).padStart(4, '0')}{returnSale.customer?.name ? ` · ${returnSale.customer.name}` : ' · Walk-in'}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: C.muted, marginTop: 4 }}>
                      Returned so far: {fmt(returnSale.returnedAmount || 0)} · Refunded so far: {fmt(returnSale.refundedAmount || 0)}
                    </div>
                  </div>

                  <div className="sl-form-group">
                    <label className="sl-form-label">Items</label>
                    <div className="sl-items-box">
                      {returnItems.map((item, index) => {
                        const available = Math.max(0, item.soldQty - item.returnedQty);
                        return (
                          <div key={item.saleItemId} className={`sl-return-item-row${index % 2 === 0 ? ' sl-item-row-even' : ''}`}>
                            <div>
                              <div style={{ fontWeight: 700, color: C.text }}>{item.name}</div>
                              <div style={{ fontSize: '0.72rem', color: C.muted, marginTop: 2 }}>
                                Sold {item.soldQty} · Returned {item.returnedQty}
                              </div>
                            </div>
                            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: C.gold, fontFamily: "'JetBrains Mono', monospace" }}>{fmt(item.unitPrice)}</div>
                            <div style={{ fontSize: '0.75rem', color: C.muted, textAlign: 'center' }}>{available} left</div>
                            <input className="sl-input" type="number" min="0" max={available} value={item.quantity} onChange={(e) => updateReturnItemQty(item.saleItemId, e.target.value)} style={{ textAlign: 'center' }} />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="sl-form-row">
                    <div className="sl-form-group">
                      <label className="sl-form-label">Reason</label>
                      <select className="sl-select-full" value={returnReason} onChange={(e) => setReturnReason(e.target.value)}>
                        {RETURN_REASONS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                      </select>
                    </div>
                    <div className="sl-form-group">
                      <label className="sl-form-label">Refund Method</label>
                      <select className="sl-select-full" value={refundMethod} onChange={(e) => setRefundMethod(e.target.value)}>
                        <option value="cash">Cash</option>
                        <option value="momo">MoMo</option>
                        <option value="card">Card</option>
                      </select>
                      <div style={{ fontSize: '0.72rem', color: C.muted, marginTop: 4 }}>Used only if this return creates money to refund immediately.</div>
                    </div>
                  </div>

                  <div className="sl-form-group">
                    <label className="sl-form-label">Notes</label>
                    <textarea className="sl-textarea" rows={2} value={returnNotes} onChange={(e) => setReturnNotes(e.target.value)} />
                  </div>
                </div>

                <div className="sl-modal-footer">
                  <button type="button" className="sl-btn-cancel" onClick={closeReturnModal}>Cancel</button>
                  <button type="submit" className="sl-btn-submit" disabled={returnLoading}>
                    {returnLoading ? 'Processing...' : 'Process Return'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        {paymentLinkSale && <PaymentLinkModal sale={paymentLinkSale} onClose={() => setPaymentLinkSale(null)} />}
      </div>
    </Layout>
  );
}
